
import Card from '@/components/base/Card';
import Button from '@/components/base/Button';
import { useState, useEffect } from 'react';

const services = [
  {
    icon: 'ri-heart-3-line',
    title: 'Facials',
    description: 'Deep cleansing, hydrating, and anti-aging facials tailored to your skin type.',
    image: 'https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/3094e8ef3880383493e9ca8d8b17bdb2.png'
  },
  {
    icon: 'ri-eye-line',
    title: 'Brows & Lashes',
    description: 'Lash lifts, tints, extensions, and professional brow shaping services.',
    image: 'https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/6637b79e08561e0c707e7ef7503bdb0f.png'
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Manicure & Pedicure',
    description: 'Relaxing mani-pedis with gel or classic finishes in a luxurious setting.',
    image: 'https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/128fe675496b724cddb83603dde677c4.png'
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Laser Treatments',
    description: 'Hair removal, skin tightening, and pigmentation correction with advanced technology.',
    image: 'https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/954f20e2d335062f36de2c35ed76723c.png'
  },
  {
    icon: 'ri-medicine-bottle-line',
    title: 'Medical Aesthetics',
    description: 'Botox, fillers, microneedling, and PRP rejuvenation treatments.',
    image: 'https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/5acedb56efdb29f149aeaba195cc3d31.png'
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Academy',
    description: 'Professional training courses for aspiring beauty and wellness practitioners.',
    image: 'https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/70ca018e74af890364f274c4f2d25c76.png'
  }
];

export default function ServicesSection() {
  const [activeSlide, setActiveSlide] = useState(0);

  const handleThumbnailClick = (index: number) => {
    setActiveSlide(index);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % services.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + services.length) % services.length);
  };

  return (
    <section id="services" className="py-20 bg-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-pink-50 rounded-full animate-float opacity-50"></div>
      <div className="absolute top-32 right-20 w-16 h-16 bg-pink-50 rounded-full animate-float-reverse opacity-40"></div>
      <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-pink-50 rounded-full animate-float opacity-30"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="relative inline-block">
            <h2 className="text-4xl lg:text-5xl font-bold text-black font-serif mb-6 animate-slide-in-scale">
              Our Services
            </h2>
            {/* Decorative underline */}
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-pink-200 rounded-full animate-expand delay-500"></div>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto animate-fade-in delay-300">
            Discover our comprehensive range of beauty and wellness treatments designed to enhance your natural radiance.
          </p>
        </div>

        {/* Story Thumbnails */}
        <div className="flex justify-center items-center gap-4 mb-12 animate-fade-in-up">
          {services.map((service, index) => (
            <button
              key={index}
              onClick={() => handleThumbnailClick(index)}
              className={`relative w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden transition-all duration-300 cursor-pointer hover:scale-110 ${
                activeSlide === index 
                  ? 'ring-4 ring-pink-300 ring-offset-2 scale-110' 
                  : 'ring-2 ring-gray-200 hover:ring-pink-200'
              }`}
            >
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover transition-all duration-300"
              />
              {/* Icon overlay */}
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                <i className={`${service.icon} text-white text-lg`}></i>
              </div>
            </button>
          ))}
        </div>

        {/* Main Slider */}
        <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden animate-fade-in-up">
          <div className="relative h-96 md:h-[500px]">
            {/* Slide Content */}
            <div className="absolute inset-0 flex transition-transform duration-500 ease-in-out"
                 style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
              {services.map((service, index) => (
                <div key={index} className="w-full flex-shrink-0 relative">
                  <div className="grid md:grid-cols-2 h-full">
                    {/* Image Side - Hidden on mobile */}
                    <div className="relative overflow-hidden hidden md:block">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
                    </div>
                    
                    {/* Content Side - Full width on mobile */}
                    <div className="flex flex-col justify-center p-8 md:p-12 bg-gradient-to-br from-pink-50/50 to-white md:col-span-1 col-span-2">
                      <div className="mb-6">
                        <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mb-6 animate-bounce-in">
                          <i className={`${service.icon} text-2xl text-black`}></i>
                        </div>
                        <h3 className="text-3xl md:text-4xl font-bold text-black mb-4 font-serif animate-slide-in-scale">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 animate-fade-in delay-300">
                          {service.description}
                        </p>
                      </div>
                      
                      <Button 
                        size="lg" 
                        href="tel:4034548778" 
                        className="self-start transform hover:scale-105 transition-all duration-300 animate-fade-in delay-500 hover-glow"
                      >
                        <span className="flex items-center whitespace-nowrap">
                          <i className="ri-calendar-line mr-2"></i>
                          Book This Service
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
            >
              <i className="ri-arrow-left-line text-xl text-black"></i>
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
            >
              <i className="ri-arrow-right-line text-xl text-black"></i>
            </button>

            {/* Navigation Dots */}
            <div className="flex justify-center space-x-2 mt-6">
              {services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === activeSlide ? 'bg-pink-400 w-6' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
